import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageInput from "./ImageInput";

export const getPath = (o, p) => p.split(".").reduce((a, k) => (a == null ? a : a[k]), o);
export const setPath = (o, p, v) => {
  const keys = p.split(".");
  const copy = { ...o };
  let cur = copy;
  keys.slice(0, -1).forEach((k) => { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; });
  cur[keys[keys.length - 1]] = v;
  return copy;
};

const inCls = "rounded-sm border-white/15 bg-[#0A192F]";
const NONE = "__none";
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 150);

const Picker = ({ value, onChange, options, testid, allowNone }) => (
  <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
    <SelectTrigger data-testid={testid} className={inCls}><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-72 border-white/15 bg-[#112240] text-white">
      {allowNone && <SelectItem value={NONE}>— None —</SelectItem>}
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Field = ({ f, value, onChange, teams, venues }) => {
  const tid = `form-${f.name.replace(".", "-")}`;
  switch (f.type) {
    case "textarea": return <Textarea data-testid={tid} rows={f.rows || 4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inCls} />;
    case "number": return <Input data-testid={tid} type="number" step="any" value={value ?? 0} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} className={inCls} />;
    case "checkbox": return <label className="flex items-center gap-3 text-sm"><Checkbox data-testid={tid} checked={!!value} onCheckedChange={(c) => onChange(!!c)} className="border-white/40 data-[state=checked]:bg-[#00E5FF] data-[state=checked]:text-[#0A192F]" />{f.label}</label>;
    case "select": return <Picker testid={tid} value={value} onChange={onChange} options={f.options.map((o) => [o, o.replace(/_/g, " ")])} />;
    case "team": return <Picker testid={tid} value={value} onChange={onChange} allowNone options={teams.map((t) => [t.id, `${String(t.number).padStart(2, "0")} · ${t.name}`])} />;
    case "venue": return <Picker testid={tid} value={value} onChange={onChange} allowNone options={venues.map((v) => [v.id, `${v.city}${v.venue_name ? ` · ${v.venue_name}` : ""}`])} />;
    case "image": return <ImageInput testid={tid} value={value} onChange={onChange} />;
    case "color": return <div className="flex items-center gap-3"><input type="color" data-testid={tid} value={value || "#00E5FF"} onChange={(e) => onChange(e.target.value)} className="h-10 w-14 cursor-pointer rounded-sm border border-white/15 bg-transparent" /><span className="font-score text-sm">{value}</span></div>;
    default: return <Input data-testid={tid} type={f.type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inCls} />;
  }
};

export default function RecordForm({ fields, value, onChange, teams, venues }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((f) => (
        <div key={f.name} className={["textarea", "image", "checkbox"].includes(f.type) ? "sm:col-span-2" : ""}>
          {f.type !== "checkbox" && <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">{f.label}</p>}
          <Field f={f} value={getPath(value, f.name)} teams={teams} venues={venues}
            onChange={(v) => {
              let next = setPath(value, f.name, v);
              const slugF = fields.find((x) => x.slugFrom === f.name);
              if (slugF && (!value[slugF.name] || value[slugF.name] === slugify(value[f.name] || ""))) next = setPath(next, slugF.name, slugify(v));
              onChange(next);
            }} />
          {f.hint && <p className="mt-1 text-xs text-slate-500">{f.hint}</p>}
        </div>
      ))}
    </div>
  );
}
